import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function DishForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    ingredients: "",
    allergens: "",
    portions_total: 10,
    price: 8.0,
    date: today(),
    cutoff_time: "11:30",
    cutoff_date: "",  // se vuoto = stesso giorno del piatto
    photo_path: null,
    pickup_location: "",
  });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/dishes/${id}`)
      .then((res) => setForm({
        name: res.data.name,
        description: res.data.description || "",
        ingredients: res.data.ingredients || "",
        allergens: res.data.allergens || "",
        portions_total: res.data.portions_total,
        price: res.data.price,
        date: res.data.date,
        cutoff_time: res.data.cutoff_time,
        cutoff_date: res.data.cutoff_date || "",
        photo_path: res.data.photo_path,
        pickup_location: res.data.pickup_location || "",
      }))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("photo_path", data.path);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        portions_total: Number(form.portions_total),
        price: Number(form.price),
        cutoff_date: form.cutoff_date || form.date,  // default = stesso giorno
      };
      if (isEdit) {
        await api.put(`/dishes/${id}`, payload);
        toast.success("Piatto aggiornato");
      } else {
        await api.post("/dishes", payload);
        toast.success("Piatto pubblicato");
      }
      navigate("/preparatore");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
      <Link to="/preparatore" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
      </Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">
        {isEdit ? "Modifica piatto" : "Nuovo piatto"}
      </h1>
      <p className="text-muted-foreground mb-8">Compila i dettagli, i colleghi vedranno l'aggiornamento subito.</p>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-lg p-6 sm:p-8">
        <Field label="Nome piatto" required>
          <input
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            data-testid="dish-name-input"
            className="input"
          />
        </Field>

        <Field label="Descrizione">
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            data-testid="dish-description-input"
            className="input resize-none"
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Ingredienti">
            <input
              value={form.ingredients}
              onChange={(e) => update("ingredients", e.target.value)}
              data-testid="dish-ingredients-input"
              placeholder="es. pasta, pomodoro, basilico"
              className="input"
            />
          </Field>
          <Field label="Allergeni">
            <input
              value={form.allergens}
              onChange={(e) => update("allergens", e.target.value)}
              data-testid="dish-allergens-input"
              placeholder="es. glutine, lattosio"
              className="input"
            />
          </Field>
        </div>

        <Field label="Punto di ritiro">
          <input
            value={form.pickup_location}
            onChange={(e) => update("pickup_location", e.target.value)}
            data-testid="dish-pickup-input"
            placeholder="es. Cucina al 2° piano, dietro al frigo"
            className="input"
          />
          <p className="text-xs text-muted-foreground mt-1">Dove i colleghi ritireranno la schiscetta.</p>
        </Field>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Porzioni" required>
            <input
              type="number"
              min={1}
              required
              value={form.portions_total}
              onChange={(e) => update("portions_total", e.target.value)}
              data-testid="dish-portions-input"
              className="input"
            />
          </Field>
          <Field label="Prezzo (€)" required>
            <input
              type="number"
              step="0.5"
              min={0}
              required
              value={form.price}
              onChange={(e) => update("price", e.target.value)}
              data-testid="dish-price-input"
              className="input"
            />
          </Field>
          <Field label="Data piatto" required>
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => update("date", e.target.value)}
              data-testid="dish-date-input"
              className="input"
            />
          </Field>
          <Field label="Ora chiusura" required>
            <input
              type="time"
              required
              value={form.cutoff_time}
              onChange={(e) => update("cutoff_time", e.target.value)}
              data-testid="dish-cutoff-input"
              className="input"
            />
          </Field>
        </div>

        <Field label="Chiusura prenotazioni — giorno (opzionale)">
          <input
            type="date"
            value={form.cutoff_date}
            max={form.date}
            onChange={(e) => update("cutoff_date", e.target.value)}
            data-testid="dish-cutoff-date-input"
            className="input"
            placeholder={form.date}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Se vuoto, la chiusura è lo stesso giorno del piatto.
            Per chiudere le prenotazioni il giorno prima, seleziona la data precedente.
          </p>
        </Field>

        <Field label="Foto piatto">
          <div className="flex items-start gap-4">
            <label className="flex-1 cursor-pointer">
              <div className="px-4 py-3 border border-dashed border-border rounded-md text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center gap-2 justify-center">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Caricamento…" : "Carica immagine (jpg, png, webp · max 5MB)"}
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onUpload}
                className="hidden"
                data-testid="dish-photo-input"
              />
            </label>
            {form.photo_path && (
              <img
                src={fileUrl(form.photo_path)}
                alt="Anteprima"
                className="w-24 h-24 object-cover rounded-md border border-border"
                data-testid="dish-photo-preview"
              />
            )}
          </div>
        </Field>

        <div className="flex gap-3 pt-3 border-t border-border">
          <Link
            to="/preparatore"
            className="flex-1 sm:flex-none px-5 py-2.5 border border-border rounded-md text-center font-medium hover:bg-muted transition-colors"
          >
            Annulla
          </Link>
          <button
            type="submit"
            disabled={saving}
            data-testid="dish-submit-button"
            className="flex-1 sm:flex-initial px-6 py-2.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] disabled:opacity-50 transition-all"
          >
            {saving ? "Salvataggio…" : isEdit ? "Aggiorna piatto" : "Pubblica piatto"}
          </button>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          padding: 0.55rem 0.75rem;
          background: white;
          border: 1px solid hsl(var(--border));
          border-radius: 0.375rem;
          font-family: "IBM Plex Sans", sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 3px hsla(120, 18%, 41%, 0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}{required && <span className="text-accent ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
